/**
 * reminderProviderNames - wording + row shaping for the "How your providers appear in messages" card on
 * Appointment Reminders -> Settings (inline reminder messages WP7, docs/plans/2026-10-17-inline-reminder-messages-plan.md
 * section 2.5c, decisions D9 + D11).
 *
 * {{apt_provider}} is filled from the appointment by teamio-utility with ONE rule that lives in the Lambda layer
 * (teamio-lambda-layer/dependencies_source_for_layer/appointmentVariables.js). appt-reminder-service runs that same rule
 * over every visible provider and appends the answer to get-apt-reminder-settings as `res[5]`:
 *   [{ ProvNum, Abbr, SpecialtyName, UpcomingAppts, DisplayName, Reason }]  (upcoming appointments first)
 * `DisplayName` '' = the provider is left out of messages; `Reason` is one of the five codes below. There is deliberately NO
 * port of the rule here - the screen only displays what the server computed, so it can never disagree with what patients get
 * (cypress/node-tests/apt-reminder-provider-names.test.js pins that every reason code the rule can return has wording).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 */

// What each reason code reads as, in grey under the name.
export const PROVIDER_REASON_TEXT = Object.freeze({
  preferred: 'Preferred name in Open Dental',
  'first-name': 'First name — hygienist or other staff',
  doctor: 'Doctor: Dr. + last name',
  'not-a-person': 'Left out — not a person',
  'no-name': 'Left out — no usable name',
});

export const PROVIDER_HINT = 'To change how a provider appears, set their Preferred Name in Open Dental.';

/**
 * The rows the card draws, in the server's order (it already puts providers with upcoming appointments first).
 * Junk in, nothing out: a missing / non-array list or a non-object row never throws.
 * @param {Array<object>} list the server's res[5]
 * @returns {Array<{key:*, abbr:string, specialty:string, upcoming:number, upcomingText:string, leftOut:boolean, appears:string, reason:string}>}
 */
export function providerRowsForDisplay(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter(row => row !== null && typeof row === 'object' && !Array.isArray(row))
    .map(row => {
      const abbr = String(row.Abbr === undefined || row.Abbr === null ? '' : row.Abbr).trim();
      const name = String(row.DisplayName === undefined || row.DisplayName === null ? '' : row.DisplayName).trim();
      const n = Number(row.UpcomingAppts);
      const upcoming = Number.isFinite(n) && n > 0 ? n : 0;
      return {
        key: row.ProvNum,
        abbr: abbr || `#${row.ProvNum}`,
        specialty: String(row.SpecialtyName === undefined || row.SpecialtyName === null ? '' : row.SpecialtyName).trim(),
        upcoming,
        upcomingText: upcoming ? `${upcoming} upcoming` : 'none upcoming',
        leftOut: !name,
        appears: name ? `with ${name}` : 'left out of messages',
        reason: PROVIDER_REASON_TEXT[row.Reason] || '',
      };
    });
}
